import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { AjsResponse, wrapAjs } from "../ajs-response";
import { buildParams, PAGINATION_FIELDS } from "../http-params.util";

/**
 * TextVariablesApiService (Angular 18).
 *
 * Replaces apps/b2b-ui/common/services/api/textVariablesApi/textVariablesApi.service.js.
 *
 * Two top-level methods on `/v2/api/textvariables/groups` and an `override`
 * sub-namespace on `/v2/api/textvariables/overrides`.
 */
const TEXT_VARIABLES_GROUPS_BASE_V2 = "/v2/api/textvariables/groups";
const TEXT_VARIABLES_OVERRIDES_BASE_V2 = "/v2/api/textvariables/overrides";

const OVERRIDE_LIST_FIELDS = [...PAGINATION_FIELDS, "archived"] as const;

interface OverridePayload {
  id?: number | string;
  name: string;
  description?: string;
  customText: unknown;
  archived?: boolean;
}

@Injectable({ providedIn: "root" })
export class TextVariablesApiService {
  private readonly http = inject(HttpClient);

  list(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(TEXT_VARIABLES_GROUPS_BASE_V2, {
        params: buildParams(props, PAGINATION_FIELDS),
      }),
    );
  }

  get(props: { groupId: number | string }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(
        `${TEXT_VARIABLES_GROUPS_BASE_V2}/${props.groupId}`,
      ),
    );
  }

  readonly override = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(TEXT_VARIABLES_OVERRIDES_BASE_V2, {
          params: buildParams(props, OVERRIDE_LIST_FIELDS),
        }),
      ),

    get: (props: { id: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(
          `${TEXT_VARIABLES_OVERRIDES_BASE_V2}/${props.id}`,
        ),
      ),

    create: (props: OverridePayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(TEXT_VARIABLES_OVERRIDES_BASE_V2, {
          name: props.name,
          description: props.description,
          customText: props.customText,
        }),
      ),

    update: (props: OverridePayload): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(TEXT_VARIABLES_OVERRIDES_BASE_V2, {
          id: props.id,
          name: props.name,
          description: props.description,
          customText: props.customText,
          archived: props.archived,
        }),
      ),

    delete: (props: { id: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.delete<unknown>(
          `${TEXT_VARIABLES_OVERRIDES_BASE_V2}/${props.id}`,
        ),
      ),
  };
}
